import { useState } from "react";
import { toast } from "sonner";
import { api, errMsg, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { useApi, post, put, Panel, PageHead, Stat, DataTable, StatusBadge, inr } from "@/crm/ui";

const STATUSES = ["all", "Pending", "Calculated", "Under Review", "Approved", "Processed", "Paid",
  "Hold", "Rejected", "Reversed"];

export default function Payouts() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const canReview = ["admin", "finance", "manager", "operations"].includes(user?.role);
  const [status, setStatus] = useState("all");
  const payouts = useApi(`/crm/payouts?status=${status}`, [status]);
  const [preview, setPreview] = useState(null);
  const [reverse, setReverse] = useState(null);
  const [reverseForm, setReverseForm] = useState({ reason: "", reversal_amount: "" });
  const [override, setOverride] = useState(false);
  const options = useApi("/crm/options");
  const [stmt, setStmt] = useState({ party_type: "employee", party_id: "", date_from: "", date_to: "" });
  const [statement, setStatement] = useState(null);
  const stmtQs = () => `${stmt.date_from ? `&date_from=${stmt.date_from}` : ""}${stmt.date_to ? `&date_to=${stmt.date_to}` : ""}`;

  const rows = payouts.data || [];
  const totals = rows.reduce((t, p) => ({
    gross: t.gross + (p.gross_commission || 0), emp: t.emp + (p.employee_share || 0),
    ptr: t.ptr + (p.partner_share || 0), inc: t.inc + (p.incentive || 0),
    net: t.net + (p.net_payable || 0),
    pending: t.pending + (["Paid", "Reversed"].includes(p.status) ? 0 : p.net_payable || 0),
  }), { gross: 0, emp: 0, ptr: 0, inc: 0, net: 0, pending: 0 });

  const act = async (row, next, extra = {}) => {
    const r = await put(`/crm/payouts/${row.payout_id}/status`,
      { status: next, stage: next, admin_override: override, ...extra }, `Payout ${next}`);
    if (r) { payouts.reload(); setOverride(false); }
  };

  const openPreview = async (row) => {
    try {
      const { data } = await api.get(`/crm/applications/${row.application_id}/commission-preview`);
      setPreview({ ...data, payout: row });
    } catch (e) { toast.error(errMsg(e)); }
  };

  const doReverse = async () => {
    const r = await post(`/crm/payouts/${reverse.payout_id}/reverse`, {
      reason: reverseForm.reason || "Disbursement reversed",
      reversal_amount: reverseForm.reversal_amount || reverse.net_payable,
    }, "Payout reversed — adjustment record created");
    if (r) { setReverse(null); setReverseForm({ reason: "", reversal_amount: "" }); payouts.reload(); }
  };

  return (
    <div className="space-y-6" data-testid="payouts-page">
      <PageHead eyebrow="Financial" title="Commission & Payouts"
                subtitle="Each payout freezes the exact commission rule version used — historical payouts never change when rules are updated. Approval, payment and reversal are Admin-only." />

      <div className="grid gap-4 sm:grid-cols-3 xl:grid-cols-6">
        <Stat label="Gross Commission" value={inr(totals.gross)} testid="payout-gross" />
        <Stat label="Employee Share" value={inr(totals.emp)} testid="payout-employee" />
        <Stat label="Partner Share" value={inr(totals.ptr)} testid="payout-partner" />
        <Stat label="Incentive" value={inr(totals.inc)} testid="payout-incentive" />
        <Stat label="Net Payable" value={inr(totals.net)} testid="payout-net" />
        <Stat label="Pending Payout" value={inr(totals.pending)} testid="payout-pending" />
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1">
          <Label className="text-xs">Status</Label>
          <select value={status} onChange={(e) => setStatus(e.target.value)}
                  data-testid="payout-status-filter"
                  className="h-9 rounded-md border border-input bg-white px-2 text-sm">
            {STATUSES.map((s) => <option key={s} value={s}>{s === "all" ? "All statuses" : s}</option>)}
          </select>
        </div>
        <Button variant="outline" className="bg-white" onClick={() => payouts.reload()}
                data-testid="payout-refresh-btn">Refresh</Button>
      </div>

      <Panel title={`Payouts (${rows.length})`} testid="payouts-panel">
        <DataTable testid="payouts-table" rows={rows} empty="No payouts yet — disburse an application to generate one."
                   columns={[
                     { key: "payout_id", label: "Payout ID" },
                     { key: "application_id", label: "Application" },
                     { key: "customer_name", label: "Lead" },
                     { key: "bank_name", label: "Bank" },
                     { key: "product_name", label: "Product" },
                     { key: "employee_id", label: "Employee" },
                     { key: "partner_id", label: "Partner" },
                     { key: "disbursed_amount", label: "Disbursed", right: true, render: (r) => inr(r.disbursed_amount) },
                     { key: "rule", label: "Rule / Version", render: (r) => (
                       <span className="text-xs">
                         {r.employee_rule_id ? `${r.employee_rule_id} v${r.employee_rule_version}` : "—"}
                         {r.partner_rule_id ? ` · ${r.partner_rule_id} v${r.partner_rule_version}` : ""}
                       </span>) },
                     { key: "employee_share", label: "Employee", right: true, render: (r) => inr(r.employee_share) },
                     { key: "partner_share", label: "Partner", right: true, render: (r) => inr(r.partner_share) },
                     { key: "incentive", label: "Incentive", right: true, render: (r) => inr(r.incentive) },
                     { key: "tds", label: "TDS", right: true, render: (r) => inr(r.tds) },
                     { key: "net_payable", label: "Net Payable", right: true, render: (r) => inr(r.net_payable) },
                     { key: "status", label: "Status", render: (r) => <StatusBadge value={r.status} /> },
                     { key: "actions", label: "Workflow", render: (r) => (
                       <span className="flex flex-wrap gap-1">
                         <Button size="sm" variant="outline" className="bg-white text-[10px]"
                                 onClick={() => openPreview(r)} data-testid={`payout-preview-${r.payout_id}`}>
                           Preview</Button>
                         {canReview && r.status === "Calculated" && (
                           <Button size="sm" variant="outline" className="bg-white text-[10px]"
                                   onClick={() => act(r, "Under Review")}
                                   data-testid={`payout-review-${r.payout_id}`}>Review</Button>)}
                         {isAdmin && ["Calculated", "Under Review", "Hold"].includes(r.status) && (
                           <Button size="sm" className="text-[10px]" onClick={() => act(r, "Approved")}
                                   data-testid={`payout-approve-${r.payout_id}`}>Approve</Button>)}
                         {isAdmin && r.status === "Approved" && (
                           <Button size="sm" variant="outline" className="bg-white text-[10px]"
                                   onClick={() => act(r, "Processed")}
                                   data-testid={`payout-process-${r.payout_id}`}>Process</Button>)}
                         {isAdmin && ["Approved", "Processed"].includes(r.status) && (
                           <Button size="sm" variant="outline" className="bg-white text-[10px]"
                                   onClick={() => act(r, "Paid", { payment_reference: `NEFT-${Date.now()}` })}
                                   data-testid={`payout-paid-${r.payout_id}`}>Mark Paid</Button>)}
                         {isAdmin && r.status !== "Reversed" && (
                           <Button size="sm" variant="outline" className="bg-white text-[10px] text-red-600"
                                   onClick={() => { setReverse(r); setReverseForm({ reason: "", reversal_amount: r.net_payable }); }}
                                   data-testid={`payout-reverse-${r.payout_id}`}>Reverse</Button>)}
                       </span>) },
                   ]} />
        {!isAdmin && (
          <p className="mt-3 text-xs text-muted-foreground" data-testid="payout-permission-note">
            Approval, payment and reversal are restricted to Admin users. Your role: {user?.role}.
          </p>)}
        {isAdmin && (
          <label className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
            <input type="checkbox" checked={override} onChange={(e) => setOverride(e.target.checked)}
                   data-testid="payout-kyc-override" />
            Admin override for unverified partner KYC (recorded in the audit trail)
          </label>)}
      </Panel>

      <Panel title="Payout statements" testid="payout-statement-panel"
             aside={<span className="text-xs text-muted-foreground">
               Period-wise statement per employee or partner · Excel &amp; PDF
             </span>}>
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1">
            <Label className="text-xs">Party type</Label>
            <select value={stmt.party_type} data-testid="statement-party-type"
                    onChange={(e) => setStmt((s) => ({ ...s, party_type: e.target.value, party_id: "" }))}
                    className="h-9 rounded-md border border-input bg-white px-2 text-sm">
              <option value="employee">Employee</option>
              <option value="partner">Partner</option>
            </select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">{stmt.party_type === "employee" ? "Employee" : "Partner"}</Label>
            <select value={stmt.party_id} data-testid="statement-party-select"
                    onChange={(e) => setStmt((s) => ({ ...s, party_id: e.target.value }))}
                    className="h-9 min-w-[220px] rounded-md border border-input bg-white px-2 text-sm">
              <option value="">Select…</option>
              {(stmt.party_type === "employee" ? options.data?.employees : options.data?.partners || [])
                ?.map((p) => (
                  <option key={p.employee_id || p.partner_id} value={p.employee_id || p.partner_id}>
                    {p.employee_name || p.partner_name}
                  </option>))}
            </select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">From</Label>
            <Input type="date" value={stmt.date_from} className="bg-white" data-testid="statement-from"
                   onChange={(e) => setStmt((s) => ({ ...s, date_from: e.target.value }))} />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">To</Label>
            <Input type="date" value={stmt.date_to} className="bg-white" data-testid="statement-to"
                   onChange={(e) => setStmt((s) => ({ ...s, date_to: e.target.value }))} />
          </div>
          <Button variant="outline" className="bg-white" disabled={!stmt.party_id}
                  data-testid="statement-preview-btn"
                  onClick={async () => {
                    try {
                      const { data } = await api.get(
                        `/crm/payout-statement/${stmt.party_type}/${stmt.party_id}?format=json${stmtQs()}`);
                      setStatement(data);
                    } catch (e) { toast.error(errMsg(e)); }
                  }}>Preview</Button>
          {["xlsx", "pdf"].map((fmt) => (
            <Button key={fmt} variant="outline" className="bg-white" disabled={!stmt.party_id}
                    data-testid={`statement-${fmt}-btn`}
                    onClick={() => window.open(
                      `${API}/crm/payout-statement/${stmt.party_type}/${stmt.party_id}?format=${fmt}${stmtQs()}`,
                      "_blank")}>
              {fmt === "xlsx" ? "Excel" : "PDF"}
            </Button>))}
        </div>
        {statement && (
          <div className="mt-5 space-y-4" data-testid="statement-preview">
            <div className="grid gap-3 sm:grid-cols-3 xl:grid-cols-6">
              {[["Total disbursement", inr(statement.totals.total_disbursement)],
                ["Gross commission", inr(statement.totals.gross_commission)],
                ["Incentive", inr(statement.totals.incentive)],
                ["TDS", inr(statement.totals.tds)],
                ["Net payable", inr(statement.totals.net_payable)],
                ["Paid / Pending", `${inr(statement.totals.paid)} / ${inr(statement.totals.pending)}`]]
                .map(([k, v]) => (
                  <div key={k} className="rounded-md border border-border p-3">
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{k}</p>
                    <p className="num text-sm font-bold">{v}</p>
                  </div>))}
            </div>
            <p className="text-xs text-muted-foreground">
              {statement.header.party_type} {statement.header.name} ({statement.header.party_id}) ·
              period {statement.header.period} · KYC {statement.header.kyc_status || "—"} ·
              reversed payouts excluded from totals ({statement.totals.reversed_payouts})
            </p>
            <DataTable testid="statement-table" rows={statement.rows} empty="No payouts in this period."
                       columns={[{ key: "payout_id", label: "Payout" },
                         { key: "application_id", label: "Application" },
                         { key: "customer_name", label: "Customer" },
                         { key: "bank_name", label: "Bank" },
                         { key: "disbursed_amount", label: "Disbursed", right: true, render: (r) => inr(r.disbursed_amount) },
                         { key: "commission_rule", label: "Rule / Version" },
                         { key: "gross_commission", label: "Commission", right: true, render: (r) => inr(r.gross_commission) },
                         { key: "incentive", label: "Incentive", right: true, render: (r) => inr(r.incentive) },
                         { key: "tds", label: "TDS", right: true, render: (r) => inr(r.tds) },
                         { key: "net_payable", label: "Net", right: true, render: (r) => inr(r.net_payable) },
                         { key: "status", label: "Status", render: (r) => <StatusBadge value={r.status} /> }]} />
          </div>)}
      </Panel>

      <Dialog open={!!preview} onOpenChange={() => setPreview(null)}>
        <DialogContent className="max-w-2xl bg-white" data-testid="commission-preview-dialog">
          <DialogHeader><DialogTitle>Commission preview — {preview?.application_id}</DialogTitle></DialogHeader>
          {preview && (
            <div className="space-y-4 text-sm">
              <div className="grid gap-2 sm:grid-cols-2">
                {[["Calculation base", inr(preview.base_amount)], ["As on", preview.as_on],
                  ["Employee commission", inr(preview.employee_commission)],
                  ["Partner commission", inr(preview.partner_commission)],
                  ["Incentive", inr(preview.incentive)], ["Gross commission", inr(preview.gross_commission)],
                  [`TDS (${preview.tds_rate}%)`, inr(preview.tds)],
                  ["Net payable", inr(preview.net_payable)]].map(([k, v]) => (
                  <p key={k} className="flex justify-between border-b border-border/60 py-1">
                    <span className="text-muted-foreground">{k}</span><b className="num">{v}</b></p>))}
              </div>
              <div className="space-y-2">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Rules applied</p>
                {preview.rules.map((r) => (
                  <div key={r.party} className="rounded-md border border-border p-3 text-xs"
                       data-testid={`preview-rule-${r.party.toLowerCase()}`}>
                    <b>{r.party}</b>{" · "}
                    {r.rule_id ? `${r.rule_id} v${r.version} · effective ${r.effective_from} · base ${r.calculation_base} · ${r.commission_type} · ${r.calculation}`
                      : r.note}
                  </div>))}
                {preview.incentive_rules.map((r) => (
                  <div key={r.incentive_id} className="rounded-md border border-border p-3 text-xs">
                    <b>{r.party} incentive</b> · {r.incentive_id} · {r.metric} · {r.incentive_type}
                  </div>))}
              </div>
            </div>)}
          <DialogFooter>
            <Button variant="outline" className="bg-white" onClick={() => setPreview(null)}
                    data-testid="preview-close-btn">Close</Button>
            {isAdmin && preview?.payout && ["Calculated", "Under Review"].includes(preview.payout.status) && (
              <Button onClick={() => { act(preview.payout, "Approved"); setPreview(null); }}
                      data-testid="preview-approve-btn">Approve payout</Button>)}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!reverse} onOpenChange={() => setReverse(null)}>
        <DialogContent className="max-w-md bg-white" data-testid="reverse-dialog">
          <DialogHeader><DialogTitle>Reverse payout {reverse?.payout_id}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">
              The original payout is retained — a reversal/adjustment record is created and audited.
            </p>
            <div className="space-y-1">
              <Label className="text-xs">Reversal amount</Label>
              <Input type="number" value={reverseForm.reversal_amount} data-testid="reverse-amount-input"
                     onChange={(e) => setReverseForm((f) => ({ ...f, reversal_amount: e.target.value }))} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Reason</Label>
              <Input value={reverseForm.reason} data-testid="reverse-reason-input"
                     onChange={(e) => setReverseForm((f) => ({ ...f, reason: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="bg-white" onClick={() => setReverse(null)}>Cancel</Button>
            <Button onClick={doReverse} data-testid="confirm-reverse-btn">Confirm reversal</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
